import { Controller } from "@hotwired/stimulus";

// The mode value for "no section expanded". A real string, not "", because
// the stored mode is read back with `||` and an empty string would revert
// to the default on the next load.
const NO_SECTION = "none"

export default class extends Controller {
  static targets = ["desktopExpand", "desktopPanel", "input", "mobilePanel", "mobileToggle", "modePanel", "modeTab", "railButton", "sectionToggle"];
  static values = {
    desktopOpen: {type: Boolean, default: false},
    storageKey: String,
    mode: {type: String, default: "people"}
  };

  initialize() {
    this.mobileOpen = false;
    this.targetsReady = false;
    this.pendingSyncTimeouts = [];
    this.handleTurboRender = this.handleTurboRender?.bind(this) || this.onTurboRender.bind(this);
  }

  connect() {
    document.addEventListener("turbo:render", this.handleTurboRender);
    document.addEventListener("turbo:load", this.handleTurboRender);
    this.desktopOpenValue = this.readDesktopState();
    this.mobileOpen = this.readMobileState();
    this.modeValue = this.readModeState();
    this.targetsReady = true;
    this.syncDesktop();
    this.syncMobile();
    this.syncMode();
  }

  desktopExpandTargetConnected() {
    if (!this.targetsReady) return;

    this.syncDesktop(false);
  }

  // A Turbo morph resets the value attributes to the server's (absent)
  // values, then reconnects targets. A sync here would persist that reset as
  // "closed" before the render handler re-reads storage -- so read first.
  desktopPanelTargetConnected() {
    if (!this.targetsReady) return;

    this.desktopOpenValue = this.readDesktopState();
    this.syncDesktop(false);
  }

  mobilePanelTargetConnected() {
    if (!this.targetsReady) return;

    this.syncMobile();
  }

  mobileToggleTargetConnected() {
    if (!this.targetsReady) return;

    this.syncMobile();
  }

  modePanelTargetConnected() {
    if (!this.targetsReady) return;

    this.modeValue = this.readModeState();
    this.desktopOpenValue = this.readDesktopState();
    this.syncMode(false);
  }

  modeTabTargetConnected() {
    if (!this.targetsReady) return;

    this.modeValue = this.readModeState();
    this.desktopOpenValue = this.readDesktopState();
    this.syncMode(false);
  }

  sectionToggleTargetConnected() {
    if (this.targetsReady) this.syncMode(false);
  }

  railButtonTargetConnected() {
    if (!this.targetsReady) return;

    this.desktopOpenValue = this.readDesktopState();
    this.modeValue = this.readModeState();
    this.syncDesktop(false);
  }

  disconnect() {
    document.removeEventListener("turbo:render", this.handleTurboRender);
    document.removeEventListener("turbo:load", this.handleTurboRender);
    this.clearPendingStateSyncs();
    this.targetsReady = false;
  }

  toggleDesktop() {
    this.desktopOpenValue = !this.desktopOpenValue;
    this.syncDesktop();
  }

  // The mobile toggle is labelled "Open planning inbox", so opening through
  // it shows the inbox whatever section was last expanded elsewhere. The
  // close controls call this with no mode and only close.
  toggleMobile(event) {
    this.mobileOpen = !this.mobileOpen;
    const mode = event?.params?.mode;
    if (this.mobileOpen && mode) {
      this.modeValue = mode;
      this.syncMode();
    }
    this.syncMobile();
  }

  // Rail click (#269). The rail is the desktop's tab strip: a button for a
  // mode that is not showing opens the panel on that mode; the open mode's
  // own button closes the panel.
  toggleMode(event) {
    const mode = event?.params?.mode || event?.currentTarget?.dataset?.canvasSidebarModeParam;
    if (!mode) return;

    // The rail: another section's button expands that section; the expanded
    // section's own button closes the panel.

    if (this.desktopOpenValue && this.modeValue === mode) {
      this.desktopOpenValue = false;
    } else {
      this.modeValue = mode;
      this.desktopOpenValue = true;
    }
    this.syncMode();
    this.syncDesktop();
  }

  // A head, or its chevron, toggles ITS section and nothing else. Collapsing
  // the expanded section leaves the sidebar open with every head in view and
  // no section expanded; closing the panel belongs to the rail. The head also
  // carries the chevron and, scoped, a link, whose own clicks are not the
  // head's.
  toggleSectionFromHead(event) {
    if (event.target.closest("button, a")) return;
    this.#toggleSection(event.params?.mode || event.currentTarget?.dataset?.canvasSidebarModeParam);
  }

  toggleSection(event) {
    this.#toggleSection(event.params?.mode || event.currentTarget?.dataset?.canvasSidebarModeParam);
  }

  #toggleSection(mode) {
    if (!mode) return;

    this.modeValue = this.modeValue === mode ? NO_SECTION : mode;
    this.desktopOpenValue = true;
    this.syncMode();
    this.syncDesktop();
  }

  // Tab click. `data-canvas-sidebar-mode-param` names the mode to show.
  switchMode(event) {
    const mode = event?.params?.mode || event?.currentTarget?.dataset?.canvasSidebarModeParam;
    if (!mode) return;

    this.modeValue = mode;
    this.syncMode();
  }

  // Show a mode and make sure the sidebar is actually visible, so a canvas-side
  // trigger (selecting a job to scope the people drawer) does not silently
  // switch a mode behind a collapsed panel.
  openWithMode(event) {
    if (event?.detail?.restored) return;
    const mode = event?.params?.mode || event?.detail?.mode;
    if (mode) this.modeValue = mode;

    this.desktopOpenValue = true;
    this.mobileOpen = true;
    this.syncDesktop();
    this.syncMobile();
    this.syncMode();
  }

  handleSubmitEnd(event) {
    if (!event.detail.success) return;

    setTimeout(() => {
      this.mobileOpen = true;
      this.syncMobile();
      this.focusInput();
    }, 0);
  }

  focusInput() {
    if (!this.hasInputTarget) return;

    const input = this.inputTargets.find(input => input.getClientRects().length > 0) || this.inputTarget;
    input.focus();
    input.select();
  }

  onTurboRender() {
    this.clearPendingStateSyncs();

    [0, 25, 100, 250].forEach(delay => {
      const timeoutId = window.setTimeout(() => {
        this.desktopOpenValue = this.readDesktopState();
        this.mobileOpen = this.readMobileState();
        this.modeValue = this.readModeState();
        this.syncDesktop();
        this.syncMobile();
        this.syncMode();
      }, delay);

      this.pendingSyncTimeouts.push(timeoutId);
    });
  }

  // `persist` is for the user's own changes. Reconnects and renders only
  // apply state: a Turbo morph resets the value attributes to the server's
  // (absent) values first, and persisting from there would record "closed".
  syncDesktop(persist = true) {
    if (persist) this.writeDesktopState();

    if (this.hasDesktopPanelTarget) {
      this.desktopPanelTarget.classList.toggle("lg:hidden", !this.desktopOpenValue);
      this.desktopPanelTarget.classList.toggle("lg:flex", this.desktopOpenValue);
    }

    if (this.hasDesktopExpandTarget) {
      this.desktopExpandTarget.classList.toggle("lg:hidden", this.desktopOpenValue);
      this.desktopExpandTarget.classList.toggle("lg:inline-flex", !this.desktopOpenValue);
    }

    // The rail marks the open mode with the plan tabs' active colour.
    this.railButtonTargets.forEach(button => {
      const active = this.desktopOpenValue && button.dataset.canvasSidebarModeName === this.modeValue;
      button.setAttribute("aria-selected", String(active));
      button.setAttribute("aria-expanded", String(active));
      // The mode tabs' active treatment: white with a soft shadow, the icon
      // in the primary colour.
      ["bg-white", "text-primary-700", "shadow-sm", "dark:bg-slate-700", "dark:text-primary-200"]
        .forEach(name => button.classList.toggle(name, active));
      ["text-slate-500", "dark:text-slate-400"]
        .forEach(name => button.classList.toggle(name, !active));
    });
  }

  syncMobile() {
    this.writeMobileState();

    if (this.hasMobilePanelTarget) {
      this.mobilePanelTarget.classList.toggle("hidden", !this.mobileOpen);
      this.mobilePanelTarget.classList.toggle("block", this.mobileOpen);
    }

    if (this.hasMobileToggleTarget) {
      this.mobileToggleTarget.setAttribute("aria-expanded", String(this.mobileOpen));
      this.mobileToggleTarget.classList.toggle("hidden", this.mobileOpen);
      this.mobileToggleTarget.classList.toggle("inline-flex", !this.mobileOpen);
    }
    this.dispatch("changed", {prefix: "canvas:sidebar"});
  }

  syncMode(persist = true) {
    if (persist) this.writeModeState();
    if (this.hasRailButtonTarget) this.syncDesktop(persist);

    // Mobile's tab strip always has a current tab: with no section expanded
    // on desktop, it falls back to its first section rather than showing
    // nothing.
    const mobileMode = this.modeValue === NO_SECTION
      ? this.modePanelTargets.find(p => !(this.hasDesktopPanelTarget && this.desktopPanelTarget.contains(p)))?.dataset.canvasSidebarModeName
      : this.modeValue;

    this.modePanelTargets.forEach(panel => {
      const inDesktopPanel = this.hasDesktopPanelTarget &&
        this.desktopPanelTarget.contains(panel);
      const active = panel.dataset.canvasSidebarModeName === (inDesktopPanel ? this.modeValue : mobileMode);
      if (inDesktopPanel) {
        // Desktop is an accordion: every section stays on screen as at least
        // its header, the active one takes the height and scrolls inside
        // itself. A collapsed section's body is display:none, so nothing in
        // it can take focus; its header must stay interactive.
        if (active) panel.setAttribute("data-expanded", "true");
        else panel.removeAttribute("data-expanded");
        this.sectionToggleTargets.filter(button => panel.contains(button)).forEach(button => {
          button.setAttribute("aria-expanded", String(active));
          button.setAttribute("aria-controls", panel.id);
        });
      } else {
        // Mobile keeps its tab strip, where one section at a time is the point.
        panel.classList.toggle("hidden", !active);
        panel.classList.toggle("flex", active);
        // A hidden mode must not keep focusable rows in the tab order.
        panel.toggleAttribute("inert", !active);
      }
    });

    this.modeTabTargets.forEach(tab => {
      const active = tab.dataset.canvasSidebarModeName === mobileMode;
      tab.setAttribute("aria-selected", String(active));
      tab.classList.toggle("bg-white", active);
      tab.classList.toggle("dark:bg-slate-700", active);
      tab.classList.toggle("text-slate-900", active);
      tab.classList.toggle("dark:text-white", active);
      tab.classList.toggle("shadow-sm", active);
      tab.classList.toggle("text-slate-500", !active);
      tab.classList.toggle("dark:text-slate-400", !active);
    });
    this.dispatch("changed", {prefix: "canvas:sidebar"});
  }

  readModeState() {
    if (!this.hasStorageKeyValue) return this.modeValue;

    return window.localStorage.getItem(this.storageKey("mode")) || this.modeValue;
  }

  writeModeState() {
    if (!this.hasStorageKeyValue) return;

    window.localStorage.setItem(this.storageKey("mode"), this.modeValue);
  }

  readMobileState() {
    return this.readStoredState("mobile", false);
  }

  readDesktopState() {
    return this.readStoredState("desktop", this.desktopOpenValue);
  }

  writeMobileState() {
    this.writeStoredState("mobile", this.mobileOpen);
  }

  writeDesktopState() {
    this.writeStoredState("desktop", this.desktopOpenValue);
  }

  readStoredState(name, fallback) {
    if (!this.hasStorageKeyValue) return fallback;

    const storedValue = window.localStorage.getItem(this.storageKey(name));
    if (storedValue == "open") return true;
    if (storedValue == "closed") return false;

    return fallback;
  }

  writeStoredState(name, open) {
    if (!this.hasStorageKeyValue) return;

    window.localStorage.setItem(this.storageKey(name), open ? "open" : "closed");
  }

  storageKey(name) {
    return `${this.storageKeyValue}-${name}`;
  }

  clearPendingStateSyncs() {
    this.pendingSyncTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.pendingSyncTimeouts = [];
  }
}
